import type { OptionsMap } from '@openx/types/options.ts';
import type { BuyerOptionsMap } from '@openx/utils/apiHooks/useBuyersFetch.ts';
import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DealParticipantsField } from '../fields.ts';

type UseCheckBuyerSeatRestrictionParams = {
  fieldPrefix: string;
  buyers?: BuyerOptionsMap;
  demandPartnerOptions?: OptionsMap;
  demandPartnerOptionsLoading?: boolean;
  showParticipantBuyersTTD?: boolean;
  showParticipantBuyersSingle?: boolean;
  showParticipantBuyersMultiple?: boolean;
};

export const SEAT_ERROR_TYPE = 'buyerSeatRestriction';
export const DSP_ERROR_TYPE = 'dspRestriction';

export const isValidSelection = (v: unknown, buyerKeySet: ReadonlySet<string>): boolean => {
  if (v == null || v === '') return true;

  if (Array.isArray(v)) {
    if (!v.length) return true;
    return v.every(item => item == null || buyerKeySet.has(item as string));
  }

  if (typeof v === 'string') {
    return buyerKeySet.has(v);
  }

  return false;
};

export const useCheckBuyerSeatRestriction = ({
  fieldPrefix,
  buyers,
  demandPartnerOptions,
  demandPartnerOptionsLoading,
  showParticipantBuyersTTD = false,
  showParticipantBuyersSingle = false,
  showParticipantBuyersMultiple = false,
}: UseCheckBuyerSeatRestrictionParams) => {
  const { t } = useTranslation();
  const { control, setError, clearErrors, getFieldState, formState } = useFormContext();

  const fieldNames = useMemo(
    () => ({
      demandPartner: `${fieldPrefix}${DealParticipantsField.DSP}`,
      mainBuyer: `${fieldPrefix}${DealParticipantsField.MAIN_BUYER}`,
      buyerId: `${fieldPrefix}${DealParticipantsField.BUYER_ID}`,
      buyerIds: `${fieldPrefix}${DealParticipantsField.BUYER_IDS}`,
    }),
    [fieldPrefix]
  );

  const activeSeatFieldName = useMemo(() => {
    if (showParticipantBuyersTTD) return fieldNames.mainBuyer;
    if (showParticipantBuyersSingle) return fieldNames.buyerId;
    if (showParticipantBuyersMultiple) return fieldNames.buyerIds;
    return null;
  }, [
    fieldNames.buyerId,
    fieldNames.buyerIds,
    fieldNames.mainBuyer,
    showParticipantBuyersMultiple,
    showParticipantBuyersSingle,
    showParticipantBuyersTTD,
  ]);

  const watchNames = useMemo(
    () => [fieldNames.demandPartner, ...(activeSeatFieldName ? [activeSeatFieldName] : [])],
    [activeSeatFieldName, fieldNames.demandPartner]
  );

  const observableWatched = useWatch({ control, name: watchNames });

  const observableDemandPartner = observableWatched?.[0] as string | null | undefined;
  const activeSeatValue = activeSeatFieldName ? observableWatched?.[1] : undefined;

  const buyerKeySet = useMemo(() => new Set(Object.keys(buyers ?? {})), [buyers]);

  useEffect(() => {
    const hasDspSelection = !!observableDemandPartner;
    const canEvaluateDsp = !!demandPartnerOptions && !demandPartnerOptionsLoading && hasDspSelection;

    const hasNoAccessToDsp = canEvaluateDsp && !demandPartnerOptions![observableDemandPartner as string];

    if (hasNoAccessToDsp) {
      const { error } = getFieldState(fieldNames.demandPartner, formState);

      if (error?.type !== DSP_ERROR_TYPE) {
        setError(fieldNames.demandPartner, {
          type: DSP_ERROR_TYPE,
          message: t(
            "Your organization doesn't have access to this DSP or it was deactivated. Please contact OpenX if you require access or choose an available DSP."
          ),
        });
      }

      [fieldNames.mainBuyer, fieldNames.buyerId, fieldNames.buyerIds].forEach(name => {
        const { error: seatError } = getFieldState(name, formState);
        if (seatError?.type === SEAT_ERROR_TYPE) {
          clearErrors(name);
        }
      });

      return;
    }

    // Only an instance that actually owns DSP-level evaluation (i.e. was given demandPartnerOptions)
    // may clear a DSP error. An instance that wasn't given demandPartnerOptions (e.g. the seat-only
    // instance mounted by ParticipantBuyers) has no way to know whether DSP access is actually fine,
    // and must not clear an error set by the sibling instance that does own DSP validation -
    // otherwise the two instances fight over the same field and loop forever (MQG-2054).
    if (demandPartnerOptions) {
      const { error } = getFieldState(fieldNames.demandPartner, formState);
      if (error?.type === DSP_ERROR_TYPE) {
        clearErrors(fieldNames.demandPartner);
      }
    }

    if (!activeSeatFieldName) return;

    const isValid = isValidSelection(activeSeatValue, buyerKeySet);
    if (!isValid) {
      const { error } = getFieldState(activeSeatFieldName, formState);

      if (error?.type !== SEAT_ERROR_TYPE) {
        setError(activeSeatFieldName, {
          type: SEAT_ERROR_TYPE,
          message: t(
            "Your organization doesn't have access to this seat or it was deactivated. Please contact OpenX if you require access or choose an available seat."
          ),
        });
      }
    } else {
      const { error } = getFieldState(activeSeatFieldName, formState);
      if (error?.type === SEAT_ERROR_TYPE) {
        clearErrors(activeSeatFieldName);
      }
    }
  }, [
    activeSeatFieldName,
    activeSeatValue,
    buyerKeySet,
    clearErrors,
    demandPartnerOptions,
    demandPartnerOptionsLoading,
    fieldNames.buyerId,
    fieldNames.buyerIds,
    fieldNames.demandPartner,
    fieldNames.mainBuyer,
    observableDemandPartner,
    setError,
    t,
    formState,
    getFieldState,
  ]);
};
